<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useDataSourcesStore } from '@/stores/dataSources'
import {
  useVueTable,
  getCoreRowModel,
  createColumnHelper,
  FlexRender
} from '@tanstack/vue-table'
import moment from 'moment'
import {
  getColumnConfig,
  getDefaultColumnWidth,
  DEFAULT_COLUMNS
} from '@/config/zeekFields'
import { generateFilterId, QUEUE_MODES, FILTER_OPERATORS } from './filters'
import FilterApplyPopup from './FilterApplyPopup.vue'
import ColumnStatsPopover from './ColumnStatsPopover.vue'

const props = defineProps({
  results: {
    type: Array,
    default: () => []
  },
  logType: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  total: {
    type: Number,
    default: 0
  },
  currentPage: {
    type: Number,
    default: 0
  },
  pageSize: {
    type: Number,
    default: 50
  },
  moreResults: {
    type: Boolean,
    default: false
  },
  showPageSizeSelector: {
    type: Boolean,
    default: true
  },
  enabledFields: {
    type: Array,
    default: null // null means show all columns (fallback to config)
  },
  fieldMappings: {
    type: Object,
    default: () => ({})
  },
  sort: {
    type: Array,
    default: () => []
  },
  fieldTypes: {
    type: Object,
    default: () => ({})
  },
  query: {
    type: String,
    default: '*'
  },
  baseQuery: {
    type: String,
    default: '*'
  },
  error: {
    type: String,
    default: ''
  },
  activeFilters: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['next-page', 'prev-page', 'apply-filter', 'sort-change', 'add-filter', 'update:page-size', 'navigate-aggregations', 'remove-filter', 'clear-filters'])

const pageSizeOptions = [50, 100, 250, 500, 750, 1000]

const router = useRouter()
const dataSourcesStore = useDataSourcesStore()

// Ctrl-click selection state
const ctrlPressed = ref(false)
const valueSelections = ref({}) // "field:rawValue" -> clickCount (1 = field-value, 2 = value-only)
const queue = ref([])
const showPopup = ref(false)
const popupSearchMode = ref(false)
const queueRef = ref(null)

// Column stats popover state
const showStatsPopover = ref(false)
const statsPopoverField = ref(null)
const statsPopoverAnchor = ref(null)
let hoverTimeout = null


// Generate a unique key for field:value combinations
function getValueKey(field, rawValue) {
  return `${field}:${JSON.stringify(rawValue)}`
}

// Track which raw values are in value-only mode (for cross-column highlighting)
// Returns a Set of JSON-stringified raw values that are selected in value-only mode
const valueOnlyRawValues = computed(() => {
  const values = new Set()
  for (const item of queue.value) {
    if (item.mode === QUEUE_MODES.VALUE_ONLY) {
      values.add(JSON.stringify(item.rawValue))
    }
  }
  return values
})

// Copy queue values to clipboard
async function copyQueueValues() {
  if (queue.value.length === 0) return
  const text = queue.value.map(item => String(item.rawValue)).join(', ')
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }
}

// Open queue as a new search
function openQueueAsSearch() {
  if (queue.value.length === 0) return

  if (queue.value.length === 1) {
    // Single item — open directly
    const item = queue.value[0]
    const escaped = String(item.rawValue).replace(/"/g, '\\"')
    const query = item.mode === QUEUE_MODES.FIELD_VALUE
      ? `${item.field}:"${escaped}"`
      : `"${escaped}"`
    const routeQuery = { query }
    const sources = dataSourcesStore.serializedSources
    if (sources) routeQuery.sources = sources
    const resolved = router.resolve({ name: 'EventSearch', query: routeQuery })
    window.open(resolved.href, '_blank')
    clearQueue()
  } else {
    // Multiple items — show popup in search mode for AND/OR choice
    popupSearchMode.value = true
    showPopup.value = true
  }
}

// Open selected IPs in IP search
const ipv4Re = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/
const ipv6Re = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/

function openQueueIpSearch() {
  const ips = [...new Set(
    queue.value
      .map(item => String(item.rawValue))
      .filter(val => ipv4Re.test(val) || ipv6Re.test(val))
  )]
  if (ips.length === 0) return
  const type = ips.length === 1 ? 'ip' : 'ip_table'
  const resolved = router.resolve({ name: 'SearchResults', query: { input: ips.join(','), type } })
  window.open(resolved.href, '_blank')
  clearQueue()
}

// Track ctrl key state
function handleKeyDown(event) {
  // Ctrl+C with queue items: copy values instead of default browser copy
  if ((event.ctrlKey || event.metaKey) && event.key === 'c' && queue.value.length > 0) {
    event.preventDefault()
    copyQueueValues()
    return
  }
  // Ctrl+S with queue items: open as new search
  if ((event.ctrlKey || event.metaKey) && event.key === 's' && queue.value.length > 0) {
    event.preventDefault()
    openQueueAsSearch()
    return
  }
  // Ctrl+I with queue items: open IPs in IP search
  if ((event.ctrlKey || event.metaKey) && event.key === 'i' && queue.value.length > 0) {
    event.preventDefault()
    openQueueIpSearch()
    return
  }
  if (event.key === 'Control' || event.key === 'Meta') {
    ctrlPressed.value = true
  }
}

function handleKeyUp(event) {
  if (event.key === 'Control' || event.key === 'Meta') {
    ctrlPressed.value = false
    // Show popup if there are items in the queue
    if (queue.value.length > 0) {
      showPopup.value = true
    }
  }
}

// Handle cell click (for ctrl-click selection)
function handleCellClick(event, row, cell) {
  // Only process if ctrl/cmd is pressed
  if (!event.ctrlKey && !event.metaKey) return

  event.preventDefault()
  event.stopPropagation()

  const colId = cell.column.id
  const rawValue = cell.getValue()

  // Skip if value is null/undefined or column is special
  if (rawValue === null || rawValue === undefined || colId === '_raw') return

  // Use field:value as the unique key (not row:col)
  // This means all cells with the same field:value are treated as one selection
  const valueKey = getValueKey(colId, rawValue)

  const currentClicks = valueSelections.value[valueKey] || 0
  const nextClicks = (currentClicks + 1) % 3 // cycle: 0 -> 1 -> 2 -> 0

  if (nextClicks === 0) {
    // Remove from queue and selection
    delete valueSelections.value[valueKey]
    queue.value = queue.value.filter(q => q.valueKey !== valueKey)
  } else {
    // Update selection
    valueSelections.value[valueKey] = nextClicks
    const mode = nextClicks === 1 ? QUEUE_MODES.FIELD_VALUE : QUEUE_MODES.VALUE_ONLY
    const formattedValue = formatCellValue(rawValue, colId)

    // Update or add to queue (only one entry per unique field:value)
    const existingIndex = queue.value.findIndex(q => q.valueKey === valueKey)
    const queueItem = {
      id: existingIndex >= 0 ? queue.value[existingIndex].id : generateFilterId(),
      field: colId,
      value: formattedValue,
      rawValue: rawValue,
      mode,
      valueKey
    }

    if (existingIndex >= 0) {
      queue.value[existingIndex] = queueItem
    } else {
      queue.value.push(queueItem)
    }
  }

  // Force reactivity update
  valueSelections.value = { ...valueSelections.value }
}

// Get selection state for a cell (based on field:value, not row:col)
// Also checks for cross-column value-only matches
function getCellSelectionState(field, rawValue) {
  // First check direct field:value match
  const valueKey = getValueKey(field, rawValue)
  const directState = valueSelections.value[valueKey] || 0

  if (directState > 0) {
    return directState
  }

  // If no direct match, check if this value is selected as value-only in another column
  // This enables cross-column highlighting for value-only selections
  const rawValueKey = JSON.stringify(rawValue)
  if (valueOnlyRawValues.value.has(rawValueKey)) {
    return 2 // Show as value-only (orange) highlight
  }

  return 0
}

// Clear the queue
function clearQueue() {
  queue.value = []
  valueSelections.value = {}
  showPopup.value = false
  popupSearchMode.value = false
}

// Handle popup apply
function handlePopupApply(operator) {
  emit('apply-filter', [...queue.value], operator)
  clearQueue()
}

// Handle popup close
function handlePopupClose() {
  showPopup.value = false
  popupSearchMode.value = false
}

// Toggle popup visibility (for clicking on queue)
function togglePopup() {
  if (queue.value.length > 0) {
    showPopup.value = !showPopup.value
  }
}

// Setup event listeners
onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('keyup', handleKeyUp)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  window.removeEventListener('keyup', handleKeyUp)
})

// Get value from object by key
function getValue(obj, key) {
  if (!obj || !key) return null
  return obj[key]
}

// Format timestamp for display
function formatTimestamp(value) {
  if (!value) return '-'

  // Handle unix timestamps (milliseconds)
  if (typeof value === 'number') {
    return moment(value).format('MMM DD, YYYY HH:mm:ss')
  }

  // Handle ISO strings
  if (typeof value === 'string') {
    const m = moment(value)
    return m.isValid() ? m.format('MMM DD, YYYY HH:mm:ss') : value
  }

  return String(value)
}

// Format bytes for display
function formatBytes(bytes) {
  if (bytes === null || bytes === undefined || bytes === '-') return '-'

  const num = Number(bytes)
  if (isNaN(num)) return bytes

  if (num === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(num) / Math.log(1024))
  const size = (num / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)

  return `${size} ${units[Math.min(i, units.length - 1)]}`
}

// Format duration for display
function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '-'

  const num = Number(seconds)
  if (isNaN(num)) return seconds

  if (num < 1) return `${(num * 1000).toFixed(0)}ms`
  if (num < 60) return `${num.toFixed(2)}s`
  if (num < 3600) return `${Math.floor(num / 60)}m ${Math.floor(num % 60)}s`

  return `${Math.floor(num / 3600)}h ${Math.floor((num % 3600) / 60)}m`
}

// Format array values
function formatArray(value) {
  if (!value) return '-'
  if (Array.isArray(value)) {
    return value.join(', ') || '-'
  }
  return String(value)
}

// Format cell value based on field type
function formatCellValue(value, key) {
  if (value === null || value === undefined) return '-'

  // Timestamp fields
  if (key === 'ts' || key.includes('timestamp') || key.includes('valid_before') || key.includes('valid_after')) {
    return formatTimestamp(value)
  }

  // Byte fields
  if (key.includes('bytes') || key === 'total_bytes') {
    return formatBytes(value)
  }

  // Duration field
  if (key === 'duration') {
    return formatDuration(value)
  }

  // Array fields
  if (Array.isArray(value)) {
    return formatArray(value)
  }

  // Boolean fields
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  // Object fields (stringify)
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}

// Format entire row as JSON (for fallback/raw display)
function formatRawDocument(row) {
  return JSON.stringify(row)
}

// Build column helper
const columnHelper = createColumnHelper()

// Build tanstack table columns from column definitions
function buildTableColumns(columnDefs) {
  return columnDefs.map(col => {
    if (col.key === '_raw') {
      return columnHelper.accessor(row => row, {
        id: '_raw',
        header: col.label,
        cell: info => formatRawDocument(info.getValue()),
        size: col.width || undefined,
        minSize: 200,
      })
    }

    return columnHelper.accessor(row => getValue(row, col.key), {
      id: col.key,
      header: col.label,
      cell: info => formatCellValue(info.getValue(), col.key),
      size: col.width || undefined,
      minSize: col.key === 'ts' ? 180 : 60,
    })
  })
}

// Generate table columns based on log type, API field mappings, and enabled fields
// enabledFields is already ordered by the parent component via useFieldSettings
const tableColumns = computed(() => {
  const logType = props.logType
  const allFields = props.fieldMappings[logType] || []

  // If no API fields available, show default columns (ts + raw document)
  if (allFields.length === 0) {
    return buildTableColumns(DEFAULT_COLUMNS)
  }

  // Use enabledFields directly - already ordered and filtered by parent
  let fieldsToShow = props.enabledFields
  if (!fieldsToShow || fieldsToShow.length === 0) {
    fieldsToShow = allFields
  }

  // Build column definitions - use field key as label, config only for width
  const columnDefs = fieldsToShow.map(fieldKey => {
    const config = getColumnConfig(logType, fieldKey)

    return {
      key: fieldKey,
      label: fieldKey,
      width: config?.width || getDefaultColumnWidth(fieldKey)
    }
  })

  return buildTableColumns(columnDefs)
})

// Create table instance
const table = useVueTable({
  get data() {
    return props.results
  },
  get columns() {
    return tableColumns.value
  },
  getCoreRowModel: getCoreRowModel(),
})

// Sort helper functions
function getSortState(fieldName) {
  const index = props.sort.findIndex(s => s.field === fieldName)
  if (index === -1) return null
  return {
    direction: props.sort[index].direction,
    priority: index + 1
  }
}

function getFieldTypeInfo(fieldName) {
  return props.fieldTypes[fieldName] || { type: 'unknown', hasKeyword: false }
}

function handleHeaderClick(fieldName) {
  const fieldInfo = getFieldTypeInfo(fieldName)
  emit('sort-change', fieldName, fieldInfo)
}

// Column stats popover handlers
function handleHeaderMouseEnter(event, fieldName) {
  // Don't show for special columns
  if (fieldName === '_raw') return

  // Clear any pending timeout
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
  }

  // Debounce: show after 400ms of hovering
  hoverTimeout = setTimeout(() => {
    statsPopoverField.value = fieldName
    statsPopoverAnchor.value = event.target.closest('th')
    showStatsPopover.value = true
  }, 400)
}

function handleHeaderMouseLeave(event) {
  // Clear pending timeout if user leaves quickly
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
    hoverTimeout = null
  }

  // Close popover unless cursor moved into it
  if (showStatsPopover.value) {
    const popoverEl = document.querySelector('.column-stats-popover')
    if (!popoverEl || !popoverEl.contains(event.relatedTarget)) {
      closeStatsPopover()
    }
  }
}

function closeStatsPopover() {
  showStatsPopover.value = false
  statsPopoverField.value = null
  statsPopoverAnchor.value = null
}

function handlePopoverAddFilter(filterData) {
  emit('add-filter', filterData)
  closeStatsPopover()
}

function handlePopoverSelectValue({ field, value }) {
  const rawValue = value
  const valueKey = getValueKey(field, rawValue)

  const currentClicks = valueSelections.value[valueKey] || 0
  const nextClicks = (currentClicks + 1) % 3

  if (nextClicks === 0) {
    delete valueSelections.value[valueKey]
    queue.value = queue.value.filter(q => q.valueKey !== valueKey)
  } else {
    valueSelections.value[valueKey] = nextClicks
    const mode = nextClicks === 1 ? QUEUE_MODES.FIELD_VALUE : QUEUE_MODES.VALUE_ONLY
    const formattedValue = formatCellValue(rawValue, field)

    const existingIndex = queue.value.findIndex(q => q.valueKey === valueKey)
    const queueItem = {
      id: existingIndex >= 0 ? queue.value[existingIndex].id : generateFilterId(),
      field,
      value: formattedValue,
      rawValue,
      mode,
      valueKey
    }

    if (existingIndex >= 0) {
      queue.value[existingIndex] = queueItem
    } else {
      queue.value.push(queueItem)
    }
  }

  valueSelections.value = { ...valueSelections.value }
}

function handleNavigateAggregations(field) {
  emit('navigate-aggregations', field)
}
</script>

<template>
  <div class="zeek-logs-table">
    <!-- Loading overlay -->
    <div v-if="loading && results.length > 0 && !error" class="loading-overlay">
      <div class="spinner"></div>
    </div>

    <!-- Error state -->
    <div v-if="error" class="state-overlay">
      <div class="state-box error-box">
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="error-icon" />
        <span class="state-message">{{ error }}</span>
      </div>
    </div>

    <!-- Loading state when no results yet -->
    <div v-else-if="loading && results.length === 0" class="state-overlay">
      <div class="state-box">
        <div class="spinner"></div>
        <span class="state-message">Loading results...</span>
      </div>
    </div>

    <!-- Empty state when query returned no results -->
    <div v-else-if="results.length === 0 && !loading" class="state-overlay">
      <div class="state-box">
        <span class="state-message">No results found</span>
        <span class="state-hint">Try adjusting your query or time range</span>
      </div>
    </div>

    <!-- Table -->
    <template v-else>
      <div class="table-container">
        <div class="table-scroll">
          <table>
            <thead>
              <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                <th
                  v-for="header in headerGroup.headers"
                  :key="header.id"
                  :style="{ width: header.getSize() ? `${header.getSize()}px` : 'auto' }"
                  :class="{ 'sortable-header': header.column.id !== '_raw' }"
                  @click="header.column.id !== '_raw' && handleHeaderClick(header.column.id)"
                  @mouseenter="handleHeaderMouseEnter($event, header.column.id)"
                  @mouseleave="handleHeaderMouseLeave($event)"
                >
                  <div class="header-content">
                    <span class="header-label">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                    </span>
                    <span class="sort-indicator" :class="{ 'sort-active': getSortState(header.column.id) }">
                      {{ getSortState(header.column.id)?.direction === 'asc' ? '▲' : '▼' }}
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody :class="{ 'ctrl-mode': ctrlPressed }">
              <tr v-for="row in table.getRowModel().rows" :key="row.id">
                <td
                  v-for="cell in row.getVisibleCells()"
                  :key="cell.id"
                  :class="{
                    'cell-timestamp': cell.column.id === 'ts',
                    'cell-selectable': ctrlPressed && cell.column.id !== '_raw',
                    'cell-selected-field': getCellSelectionState(cell.column.id, cell.getValue()) === 1,
                    'cell-selected-value': getCellSelectionState(cell.column.id, cell.getValue()) === 2
                  }"
                  @click="handleCellClick($event, row, cell)"
                >
                  <div class="cell-content">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Table Footer -->
      <div class="table-footer">
        <!-- Filter Queue (left side) -->
        <div class="footer-left">
          <div
            v-if="queue.length > 0"
            ref="queueRef"
            class="filter-queue"
            :class="{ 'popup-open': showPopup }"
            @click="togglePopup"
          >
            <span class="queue-count">{{ queue.length }}</span>
            <span class="queue-label">{{ queue.length === 1 ? 'field' : 'fields' }} selected</span>
            <button class="queue-clear" @click.stop="clearQueue" title="Clear selection">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>
        </div>

        <!-- Pagination (center) -->
        <div class="pagination-controls">
          <button
            @click="$emit('prev-page')"
            :disabled="loading || currentPage === 0"
            class="btn-pagination"
          >
            <font-awesome-icon :icon="['fas', 'angle-left']" />
          </button>
          <span class="result-info">
            {{ currentPage * pageSize + 1 }}-{{ Math.min((currentPage + 1) * pageSize, total) }} of {{ total.toLocaleString() }}
          </span>
          <button
            @click="$emit('next-page')"
            :disabled="loading || !moreResults"
            class="btn-pagination"
          >
            <font-awesome-icon :icon="['fas', 'angle-right']" />
          </button>
        </div>

        <!-- Right side (active filters + page size) -->
        <div class="footer-right">
          <div v-if="activeFilters.length > 0" class="active-filters">
            <div
              v-for="filter in activeFilters"
              :key="filter.id"
              class="filter-tag"
            >
              <span class="filter-field">{{ filter.field }}:</span>
              <span class="filter-value">{{ filter.displayValue }}</span>
              <button class="filter-remove" @click="emit('remove-filter', filter.id)">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
            <button v-if="activeFilters.length > 1" class="clear-all-btn" @click="emit('clear-filters')">
              Clear all
            </button>
          </div>
          <div v-if="showPageSizeSelector" class="dropup">
            <button
              class="btn-page-size dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {{ pageSize }} rows
            </button>
            <ul class="dropdown-menu dropdown-menu-end page-size-menu">
              <li v-for="size in pageSizeOptions" :key="size">
                <button
                  class="dropdown-item"
                  :class="{ active: size === pageSize }"
                  @click="emit('update:page-size', size)"
                >
                  {{ size }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>

    <!-- Filter Apply Popup -->
    <FilterApplyPopup
      :show="showPopup"
      :queue="queue"
      :anchor-el="queueRef"
      :initial-search-mode="popupSearchMode"
      @apply="handlePopupApply"
      @close="handlePopupClose"
      @clear="clearQueue"
    />

    <!-- Column Stats Popover -->
    <ColumnStatsPopover
      :show="showStatsPopover"
      :field="statsPopoverField"
      :query="query"
      :log-types="logType ? [logType] : []"
      :anchor-el="statsPopoverAnchor"
      :get-selection-state="getCellSelectionState"
      :ctrl-active="ctrlPressed"
      @add-filter="handlePopoverAddFilter"
      @select-value="handlePopoverSelectValue"
      @close="closeStatsPopover"
      @navigate-aggregations="handleNavigateAggregations"
    />
  </div>
</template>

<style scoped>
.zeek-logs-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #151a22;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

/* State Overlay (Loading/Empty) */
.state-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 3rem;
  background: #1a1f27;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.state-message {
  color: #E5E7EB;
  font-size: 0.95rem;
  text-align: center;
}

.state-hint {
  color: #6B7280;
  font-size: 0.8rem;
  text-align: center;
}

/* Error State */
.error-box {
  border-color: rgba(158, 74, 74, 0.4);
}

.error-icon {
  font-size: 2rem;
  color: #EF4444;
  opacity: 0.8;
}

/* Spinner */
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #374151;
  border-top: 3px solid #7357D2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Table Container */
.table-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
}

/* Scrollbar styling */
.table-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4A5661 0%, #3E4A56 100%);
  border-radius: 10px;
  border: 2px solid rgba(0, 0, 0, 0.2);
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5A6671 0%, #4E5A66 100%);
}

.table-scroll::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0.2);
}

/* Table Styling */
table {
  min-width: 100%;
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
}

thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

thead th {
  background-color: #151a22;
  color: #9CA3AF;
  text-align: left;
  padding: 1rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Sortable Headers */
thead th.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

thead th.sortable-header:hover {
  background-color: #1d2531;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
}

.header-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sort-indicator {
  color: #F59E0B;
  font-size: 0.65rem;
  flex-shrink: 0;
  margin-left: auto;
  opacity: 0;
  min-width: 0.65rem;
}

.sort-indicator.sort-active {
  opacity: 1;
}

tbody td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #E5E7EB;
  font-size: 0.85rem;
  vertical-align: top;
}

tbody tr {
  transition: background-color 0.15s ease;
}

tbody tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.015);
}

tbody tr:nth-child(odd) {
  background-color: transparent;
}

tbody tr:hover {
  background-color: rgba(115, 87, 210, 0.08) !important;
}

/* Cell Content */
.cell-content {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Timestamp cell */
.cell-timestamp {
  color: #9CA3AF;
  font-family: 'Cutive Mono', monospace;
  font-weight: 500;
}

/* Ctrl Mode - disable text selection */
tbody.ctrl-mode {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

tbody.ctrl-mode td {
  cursor: pointer;
}

/* Cell Selection States */
.cell-selectable {
  cursor: pointer;
  transition: all 0.15s ease;
}

.cell-selectable:hover {
  background: rgba(115, 87, 210, 0.15) !important;
}

.cell-selected-field {
  background: rgba(115, 87, 210, 0.12) !important;
  border-left: 3px solid #7357D2 !important;
  padding-left: calc(1rem - 3px);
}

.cell-selected-value {
  background: rgba(245, 158, 11, 0.12) !important;
  border-left: 3px solid #F59E0B !important;
  padding-left: calc(1rem - 3px);
}

/* Table Footer */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
  background: #151a22;
}

.footer-left,
.footer-right {
  flex: 1;
  min-width: 0;
}

.footer-left {
  display: flex;
  justify-content: flex-start;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
}

.btn-page-size {
  padding: 0.4rem 0.6rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #9CA3AF;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.8rem;
}

.btn-page-size:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
}

.page-size-menu {
  background-color: #1e252f;
  border: 1px solid #232a35;
  min-width: 5rem;
}

.page-size-menu .dropdown-item {
  color: #9CA3AF;
  font-size: 0.8rem;
  padding: 0.35rem 0.75rem;
}

.page-size-menu .dropdown-item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
}

.page-size-menu .dropdown-item.active {
  background: rgba(115, 87, 210, 0.2);
  color: #a78bfa;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.pagination-controls .btn-pagination {
  padding: 0.4rem 0.6rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #9CA3AF;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-controls .btn-pagination:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
}

.pagination-controls .btn-pagination:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.result-info {
  color: #6B7280;
  font-size: 0.8rem;
}

/* Filter Queue */
.filter-queue {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.filter-queue:hover,
.filter-queue.popup-open {
  background: rgba(115, 87, 210, 0.25);
  border-color: #7357D2;
}

.queue-count {
  background: #7357D2;
  color: white;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 1.25rem;
  text-align: center;
}

.queue-label {
  color: #E5E7EB;
  font-size: 0.75rem;
}

.queue-clear {
  background: none;
  border: none;
  color: #6B7280;
  padding: 0.15rem;
  cursor: pointer;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  margin-left: 0.25rem;
}

.queue-clear:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #EF4444;
}

/* Active Filters */
.active-filters {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-tag {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.25rem;
  font-size: 0.625rem;
}

.filter-field {
  color: #9CA3AF;
}

.filter-value {
  color: #E5E7EB;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-remove {
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  font-size: 0.5rem;
  border-radius: 0.125rem;
  transition: all 0.15s ease;
}

.filter-remove:hover {
  color: #EF4444;
  background: rgba(239, 68, 68, 0.2);
}

.clear-all-btn {
  background: none;
  border: none;
  color: #6B7280;
  font-size: 0.625rem;
  cursor: pointer;
  padding: 0.125rem 0.25rem;
  transition: color 0.15s ease;
}

.clear-all-btn:hover {
  color: #EF4444;
}
</style>
